import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

const eventsFormSchema = z.object({
  lookback_time_in_minutes: z.number(),
  soundAlertLevels: z.object({
    note: z.boolean(),
    warning: z.boolean(),
    alert: z.boolean(),
  }),
});

export type EventsFormValues = z.infer<typeof eventsFormSchema>;

export const useEventsForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { mutate: mutateSettings } = useSaveSettings();
  const { setUnsavedSettings } = useSettingsPage();

  const initialValues: EventsFormValues = useMemo(
    () => ({
      lookback_time_in_minutes:
        settings['ribbonPlugin.settings']?.event_lookback_minutes || 720,
      soundAlertLevels: {
        note:
          settings['ribbonPlugin.settings']?.sound_alert_levels?.note ?? false,
        warning:
          settings['ribbonPlugin.settings']?.sound_alert_levels?.warning ??
          false,
        alert:
          settings['ribbonPlugin.settings']?.sound_alert_levels?.alert ?? false,
      },
    }),
    [settings]
  );

  const form = useForm<EventsFormValues>({
    resolver: zodResolver(eventsFormSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  const onSubmit = (data: EventsFormValues) => {
    setIsSubmitting(true);

    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        event_lookback_minutes: data.lookback_time_in_minutes,
        sound_alert_levels: {
          note: data.soundAlertLevels.note,
          warning: data.soundAlertLevels.warning,
          alert: data.soundAlertLevels.alert,
        },
      },
    };

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-events-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsEventsSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-events-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsEventsFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  };

  return {
    form,
    onSubmit,
    isSubmitting,
  };
};
